import { Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AppConstants } from '../../../../app.constants';
import { AuthenticationsService } from '../../../../modules/authentications/authentications.service';
import { AuthenticationLoginResponseDto } from '../../../../modules/authentications/dtos/authentication-login-response';
import { BeneficiariesService } from '../../../../modules/beneficiaries/beneficiaries.service';
import { BeneficiaryDto } from '../../../../modules/beneficiaries/dtos/beneficiary';
import { BeneficiaryDependentDto } from '../../../../modules/beneficiaries/dtos/beneficiary-dependent';
import { BeneficiaryInfoDetailsDto } from '../../../../modules/beneficiaries/dtos/beneficiary-info-details';
import { BeneficiaryPolicyDto } from '../../../../modules/beneficiaries/dtos/beneficiary-policy';
import { BeneficiaryWalletDto } from '../../../../modules/beneficiaries/dtos/beneficiary-wallet';
import { WalletDeleteDto } from '../../../../modules/wallets/dtos/wallet-delete';
import { WalletsService } from '../../../../modules/wallets/wallets.service';
import { SnackBarService } from '../../../../shared/components/snack-bar/snack-bar.service';
import {
	UPLOAD_EXTENSIONS,
	UPLOAD_MAX_FILES,
} from '../../../../shared/constants/constants';
import {
	UPLOAD_FILE_ERROR_MESSAGE,
	UPLOAD_FORMAT_ERROR_MESSAGE,
	UPLOAD_MAX_FILES_MESSAGE,
	UPLOAD_MAX_FILE_SIZE_MESSAGE,
} from '../../../../shared/constants/error-message';
import { TypeKinship } from '../../../../shared/enums/type-kinship.enum';
import { WalletType } from '../../../../shared/enums/wallet-type.enum';
import { RouteUtils } from '../../../../shared/utils/route/route.util';
import { EnumUtils } from '../../../../utils/enum.utils';
import { PhoneUtils } from '../../../../utils/phone-utils';
import { INotification } from '../../models/notification';
import { MessageResponse } from '../../models/organizations.model';
import {
	deleteBeneficiaryNotificationStart,
	fetchBeneficiaryAllPoliciesStart,
	fetchBeneficiaryViewClear,
	fetchBeneficiaryAllBenefitsStart,
	fetchBeneficiaryViewStart,
	fetchBeneficiaryFileURLStart,
	fetchBeneficiaryFileURLClear,
	editBeneficiaryStatusClear,
	editBeneficiaryStatusStart,
} from '../../state/organizations.actions';
import {
	selectBeneficiaryAllBenefits,
	selectBeneficiaryAllPolicies,
	selectBeneficiaryFileURL,
	selectBeneNewStatus,
	selectNotificationsResult,
} from '../../state/organizations.selector';

import { AddCongenerBenefitModalComponent } from './add-congener-benefit-modal/add-congener-benefit-modal.component';
import { CreateEditDependentModalComponent } from './create-edit-dependent-modal/create-edit-dependent-modal.component';
import { DeleteDependentModalComponent } from './delete-dependent-modal/delete-dependent-modal.component';
import { EditBenefitsModalComponent } from './edit-benefits-modal/edit-benefits-modal.component';
import { EditPoliciesModalComponent } from './edit-policies-modal/edit-policies-modal.component';
import { ManageBenefitDateModalComponent } from './manage-benefit-date-modal/manage-benefit-date-modal.component';

@Component({
	selector: 'app-beneficiary-attach-dialog',
	template: `
		<app-dialog
			modalTitle="Atenção"
			titleButton="Ok"
			[secButton]="false"
			[asAlert]="true"
			(dialogClose)="dialogRef.close()"
			(dialogSave)="dialogRef.close()"
		>
			<p class="lh-md">{{ data }}</p>
		</app-dialog>
	`,
})
export class BeneficiaryAttachDialogComponent {
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: string,
		public dialogRef: MatDialogRef<BeneficiaryAttachDialogComponent>,
	) {}
}

@Component({
	selector: 'app-beneficiary-view',
	styleUrls: ['./beneficiary-view.component.scss'],
	templateUrl: './beneficiary-view.component.html',
})
export class BeneficiaryViewComponent implements OnInit, OnDestroy {
	alertRef!: MatDialogRef<BeneficiaryAttachDialogComponent>;
	allPolicies: any;
	beneficiary!: BeneficiaryDto;

	beneficiaryFiles: File[] = [];
	beneficiaryId: string = '';
	beneficiaryType: string = '';
	benefitWallets: BeneficiaryWalletDto[] = [];
	cfgForm: UntypedFormGroup;
	conjugatedBenefitData: BeneficiaryWalletDto | null = null;

	dateFilter = (date: Date | null): boolean => {
		const newDate = new Date(date ?? '');
		const year = newDate.getFullYear();

		return year > 2015 && year < 2032;
	};

	dentalBenefitData: BeneficiaryWalletDto | null = null;
	dentalBenefits: BeneficiaryInfoDetailsDto[] = [];
	dentalCongenerBenefitData: BeneficiaryWalletDto | null = null;
	dentalPolicies: BeneficiaryInfoDetailsDto[] = [];
	dentalPolicyData: BeneficiaryPolicyDto | null = null;

	dependentTypes: string[] = [
		'Cônjuge',
		'Filho',
		'Enteado',
		'Pai / Mãe',
		'Neto',
		'Irmão',
		'Avô / Avó',
		'Tio / Tia',
		'Sobrinho',
		'Bisneto',
		'Sogro',
		'Genro / Nora',
		'Padrasto / Madrasta',
	];

	dialogRef!: MatDialogRef<
		| AddCongenerBenefitModalComponent
		| CreateEditDependentModalComponent
		| EditBenefitsModalComponent
		| EditPoliciesModalComponent
		| ManageBenefitDateModalComponent
	>;
	existingFiles: { id: string; name: string }[] = [];
	extensionErrorMessage = UPLOAD_FORMAT_ERROR_MESSAGE;
	extensions = UPLOAD_EXTENSIONS;

	extensionsMessage = `Extensões: ${UPLOAD_EXTENSIONS.join(', ')}`;
	fileSizeLabel = UPLOAD_MAX_FILE_SIZE_MESSAGE;
	filesOverA: boolean = false;

	hasDentalBenefit: boolean = false;
	hasExtensionError: boolean = false;
	hasHealthBenefit: boolean = false;

	hasSizeError: boolean = false;

	healthBenefitData: BeneficiaryWalletDto | null = null;
	healthBenefits: BeneficiaryInfoDetailsDto[] = [];
	healthCongenerBenefitData: BeneficiaryWalletDto | null = null;
	healthPolicies: BeneficiaryInfoDetailsDto[] = [];
	healthPolicyData: BeneficiaryPolicyDto | null = null;
	inputId: string = String(Math.random());
	insuranceStatus: string = '';
	isDeleteButtonVisible: boolean = false;
	isDentalCongenerWalletPending?: boolean = false;
	isDentalWalletPending?: boolean = false;
	isHealthCongenerWalletPending?: boolean = false;

	isHealthWalletPendent?: boolean = false;
	isLoading: boolean = false;

	isLoadingBenefits: boolean = false;
	isLoadingFiles: boolean = false;

	maxFilesPerUploadMessage = UPLOAD_MAX_FILES_MESSAGE;

	organizationId: string = '';

	pendencyDentalBenefit: boolean = false;
	pendencyDentalCard: boolean = false;
	pendencyDentalPolicy: boolean = false;

	pendencyHealthBenefit: boolean = false;

	pendencyHealthCard: boolean = false;

	pendencyHealthPolicy: boolean = false;
	phoneMask = (value: string): string => {
		return PhoneUtils.phoneMask(value);
	};
	policies: any;
	session: AuthenticationLoginResponseDto | null =
		this.authenticationsService.getSession();
	sizeErrMsg = UPLOAD_FILE_ERROR_MESSAGE;
	userOrganizationId?: string;
	wallets: any;
	_notification$!: Observable<INotification[]>;

	private onDeleteDependentSubscription$: Subscription = new Subscription();
	private onRemoveFileSubscription$: Subscription = new Subscription();
	private onSaveFilesSubscription$: Subscription = new Subscription();
	private searchSubscription$: Subscription = new Subscription();
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly beneficiariesService: BeneficiariesService,
		private readonly dialog: MatDialog,
		private readonly fb: UntypedFormBuilder,
		private readonly snackbarService: SnackBarService,
		private readonly router: Router,
		private readonly store: Store,
		private readonly location: Location,
		private readonly authenticationsService: AuthenticationsService,
		private readonly walletsService: WalletsService,
	) {
		this.cfgForm = this.fb.group({
			active: [null, [Validators.required]],
			inactived_at: [null],
			is_deleted: [false],
			retired_at: [null],
		});
		this.userOrganizationId = this.session?.organization?.id;
		this.isDeleteButtonVisible =
			this.userOrganizationId === AppConstants.organization.id;
	}

	addCongenerPlan(): void {
		this.dialogRef = this.dialog.open(AddCongenerBenefitModalComponent, {
			data: {
				beneficiaryId: this.beneficiaryId,
				existingDentalCongener: !!this.dentalCongenerBenefitData,
				existingHealthCongener: !!this.healthCongenerBenefitData,
				organizationId: this.organizationId,
				positionId: this.beneficiary.position.id,
				titularId: this.beneficiary.titular?.id,
				typeKinship: this.beneficiary.typeKinship,
			},
			disableClose: true,
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.searchBeneficiary();
			}
		});
	}

	downloadFile(url: string): void {
		const link = document.createElement('a');
		link.setAttribute('target', '_blank');
		link.setAttribute('href', url);
		link.setAttribute('download', 'file_name.pdf');
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	filterDependentBenefits(
		benefits: BeneficiaryWalletDto[],
		isHealth: boolean = true,
	): BeneficiaryWalletDto | null {
		if (isHealth) {
			return benefits.find((benefit) => benefit.is_health) || null;
		} else {
			return benefits.find((benefit) => benefit.is_dental) || null;
		}
	}

	filterNotifications(): void {
		if (this.beneficiary.notifications.length > 0) {
			this.beneficiary.notifications?.forEach((notification) => {
				switch (notification.type_message) {
					case 'w1':
					case 'w3':
						this.pendencyHealthBenefit = true;
						break;
					case 'w2':
					case 'w4':
						this.pendencyDentalBenefit = true;
						break;
					case 'w5':
						this.pendencyHealthCard = true;
						break;
					case 'w6':
						this.pendencyDentalCard = true;
						break;
					case 'p1':
					case 'p4':
						this.pendencyDentalPolicy = true;
						break;
					case 'p2':
					case 'p3':
						this.pendencyHealthPolicy = true;
						break;
					case 'x2': {
						this.pendencyHealthBenefit = true;
						this.pendencyDentalBenefit = true;
						this.pendencyHealthPolicy = true;
						this.pendencyDentalPolicy = true;
						break;
					}
					default:
						this.pendencyHealthBenefit = false;
						this.pendencyDentalBenefit = false;
						this.pendencyHealthPolicy = false;
						this.pendencyDentalPolicy = false;
				}
			});
		}
	}

	formattedPhone(value: string): string {
		return PhoneUtils.sanitize(value);
	}

	getDependentType(dependentType: number): string {
		if (!dependentType) {
			return '';
		}

		return (
			EnumUtils.getKinship(
				EnumUtils.getKinshipFromNumberValue(dependentType),
			) ?? ''
		);
	}

	getTitularUrl(): string {
		return `/organizacoes/${this.organizationId}/beneficiarios/${
			this.beneficiary.titular?.id
		}`;
	}

	isTitular(): boolean {
		return this.beneficiary.typeKinship === TypeKinship.TITULAR;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.onDeleteDependentSubscription$.unsubscribe();
		this.onSaveFilesSubscription$.unsubscribe();
		this.searchSubscription$.unsubscribe();
		this.onRemoveFileSubscription$.unsubscribe();
		this.store.dispatch(fetchBeneficiaryViewClear());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, bId, bt }) => {
			this.organizationId = id;
			this.beneficiaryType = bt;
			this.beneficiaryId = bId;

			this._notification$ = this.store.select(selectNotificationsResult);

			this.isLoadingBenefits = true;
			this.searchBeneficiary();
		});
	}

	onActivenessChange(active: boolean): void {
		if (active) {
			this.cfgForm.get('inactived_at')!.setValue(null);
			this.cfgForm.get('is_deleted')!.setValue(null);
			this.cfgForm.get('retiredAt')!.setValue(null);
		}

		const validators = active ? [] : [Validators.required];
		this.cfgForm.get('inactived_at')!.setValidators(validators);
		this.cfgForm.get('inactived_at')!.updateValueAndValidity();
	}

	onAddFiles(event: any) {
		let files = event.target.files;

		for (let i = 0; i < files.length && i < UPLOAD_MAX_FILES; i++) {
			this.beneficiaryFiles.push(files.item(i));
		}

		event.target.value = null;

		if (this.beneficiaryFiles.length) {
			this.onSaveFiles();
		}
	}

	onBenefitsModalOpen(): void {
		if (
			this.beneficiaryType !== 'inativo' &&
			this.beneficiaryType !== 'aposentado'
		) {
			this.dialogRef = this.dialog.open(EditBenefitsModalComponent, {
				data: {
					beneficiaryId: this.beneficiaryId,
					conjugated: this.conjugatedBenefitData,
					dental: this.dentalBenefitData,
					dentalCongener: this.dentalCongenerBenefitData,
					health: this.healthBenefitData,
					healthCongener: this.healthCongenerBenefitData,
					orgId: this.organizationId,
					positionId: this.beneficiary.position.id,
					titularId: this.beneficiary.titular?.id,
					typeKinship: this.beneficiary.typeKinship,
				},
				disableClose: true,
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.searchBeneficiary();
				}
			});
		}
	}

	onDeleteBenefitModal(benefit: BeneficiaryWalletDto): void {
		this.dialogRef = this.dialog.open(ManageBenefitDateModalComponent, {
			data: {
				benefitName: benefit.name,
				isConjugated: benefit.type === WalletType.CONJUGATED,
			},
			disableClose: true,
			maxWidth: undefined,
		});
		this.dialogRef
			.afterClosed()
			.subscribe((deletedInInsuranceAt: string | null) => {
				if (deletedInInsuranceAt) {
					this.isLoading = true;
					if (benefit.type === WalletType.CONJUGATED) {
						const conjugatedPlanName = benefit.name;
						const walletsToDelete = this.beneficiary.benefits.filter(
							(b) =>
								b.type === WalletType.CONJUGATED &&
								b.name === conjugatedPlanName,
						);

						if (walletsToDelete.length > 0) {
							const dtos: WalletDeleteDto[] = walletsToDelete.map(
								(walletPart) =>
									new WalletDeleteDto({
										deletedInInsuranceAt: deletedInInsuranceAt,
										walletId: walletPart.id,
									}),
							);
							this.walletsService.deleteManyWallets({ dto: dtos }).subscribe(
								(response: MessageResponse) => {
									this.snackbarService.showSnack(
										response?.message ?? 'Benefício removido com sucesso.',
										response?.type ?? 'success',
									);
									this.searchBeneficiary();
								},
								(error) => {
									this.snackbarService.showSnack(
										error.error?.message || 'Error deleting benefits',
										'error',
									);
									this.isLoading = false;
								},
							);
						} else {
							this.snackbarService.showSnack(
								'Could not find conjugated benefit parts to delete.',
								'error',
							);
							this.isLoading = false;
						}
					} else {
						const dto = new WalletDeleteDto({
							deletedInInsuranceAt: deletedInInsuranceAt,
							walletId: benefit.id,
						});
						this.walletsService.deleteOneWallet({ dto }).subscribe(
							(response: MessageResponse | null) => {
								this.snackbarService.showSnack(
									response?.message ?? 'Benefício removido com sucesso.',
									response?.type ?? 'success',
								);
								this.searchBeneficiary();
							},
							(error) => {
								this.snackbarService.showSnack(
									error.error?.message || 'Error deleting benefit',
									'error',
								);
							},
						);
					}
				}
			});
	}

	onDeleteDependent(dependent: BeneficiaryDependentDto): void {
		const dependentId: string = dependent.id;
		const cpf: string = dependent.user.cpf;
		const name: string = dependent?.user?.name?.toCapitalizedCase();

		const dialogRef = this.dialog.open(DeleteDependentModalComponent, {
			data: { name: name },
			maxWidth: undefined,
		});

		dialogRef.afterClosed().subscribe((result: string) => {
			if (result) {
				this.onDeleteDependentSubscription$ = this.beneficiariesService
					.deleteDependent({
						dependentId: dependentId,
						dto: {
							cpf: cpf,
							dateOfExclusion: result,
						},
						organizationId: this.organizationId,
					})
					.subscribe(
						async (response: MessageResponse) => {
							this.snackbarService.showSnack(response?.message, response?.type);
							this.searchBeneficiary();
						},
						(error) => {
							this.snackbarService.showSnack(error.error?.message, 'error');
						},
					);
			}
		});
	}

	onDeleteNotification(id: string): void {
		this.store.dispatch(deleteBeneficiaryNotificationStart({ id }));

		setTimeout(() => {
			this.searchBeneficiary();
			this.store.dispatch(
				fetchBeneficiaryViewStart({
					id: this.organizationId,
					beneficiaryId: this.beneficiaryId,
					beneficiaryType: this.beneficiaryType,
				}),
			);
		}, 300);
	}

	onDependentBenefitModalOpen(dependent: BeneficiaryDependentDto): void {
		if (
			this.beneficiaryType !== 'inativo' &&
			this.beneficiaryType !== 'aposentado'
		) {
			const conjugated =
				dependent.benefits.find((b) => b.type === WalletType.CONJUGATED) ||
				null;
			const dental =
				dependent.benefits.find(
					(b) => b.is_dental && b.type === WalletType.DEFAULT,
				) || null;
			const dentalCongener =
				dependent.benefits.find(
					(b) => b.is_dental && b.type === WalletType.CONGENER,
				) || null;
			const health =
				dependent.benefits.find(
					(b) => b.is_health && b.type === WalletType.DEFAULT,
				) || null;
			const healthCongener =
				dependent.benefits.find(
					(b) => b.is_health && b.type === WalletType.CONGENER,
				) || null;

			this.dialogRef = this.dialog.open(EditBenefitsModalComponent, {
				data: {
					beneficiaryId: dependent.id,
					conjugated: conjugated,
					dental: dental,
					dentalCongener: dentalCongener,
					health: health,
					healthCongener: healthCongener,
					orgId: this.organizationId,
					positionId: this.beneficiary.position.id,
					titularId: this.beneficiary.id,
					typeKinship: dependent.typeKinship,
				},
				disableClose: true,
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.searchBeneficiary();
				}
			});
		}
	}

	onDependentModalOpen(depId: string | null): void {
		if (
			this.beneficiaryType !== 'inativo' &&
			this.beneficiaryType !== 'aposentado'
		) {
			this.dialogRef = this.dialog.open(CreateEditDependentModalComponent, {
				data: {
					cpf: this.beneficiary.user.cpf,
					dependentId: depId,
					organizationId: this.organizationId,
				},
				disableClose: true,
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result) => {
				if (result?.reload) {
					this.searchBeneficiary();
				}
			});
		}
	}

	onDraggedFiles(event: any): void {
		event.stopPropagation();
		event.preventDefault();
		this.filesOverA = true;
	}

	onDroppedFiles(event: any): void {
		event.preventDefault();
		event.stopPropagation();
		this.filesOverA = false;
		let files = event.dataTransfer.files;

		for (let i = 0; i < files.length; i++) {
			if (this.beneficiaryFiles.length + this.beneficiary.files.length <= 9) {
				this.beneficiaryFiles.push(files.item(i));
			}
		}
	}

	onEditRegistration(): void {
		if (!(!this.organizationId || !this.beneficiaryId)) {
			this.router.navigate([
				'organizacoes',
				this.organizationId,
				'beneficiarios',
				'editar',
				this.beneficiaryId,
			]);
		}
	}

	onFileDownload(id: string): void {
		this.store.dispatch(fetchBeneficiaryFileURLStart({ fileId: id }));
	}

	onLeaveDraggedFiles(event: any): void {
		event.stopPropagation();
		event.preventDefault();
		this.filesOverA = false;
	}

	onNavBack(): void {
		if (this.organizationId) {
			const params = this.location.getState() as unknown as {
				insuranceStatus: string;
			};
			this.router.navigate(
				['organizacoes', this.organizationId, 'beneficiarios'],
				{
					queryParams: {
						statusSeguradora: params?.insuranceStatus,
					},
				},
			);
		}
	}

	onPoliciesModalOpen(): void {
		if (
			this.beneficiaryType !== 'inativo' &&
			this.beneficiaryType !== 'aposentado'
		) {
			this.dialogRef = this.dialog.open(EditPoliciesModalComponent, {
				data: {
					beneficiaryId: this.beneficiaryId,
					dental: this.dentalPolicyData,
					health: this.healthPolicyData,
					orgId: this.organizationId,
					positionId: this.beneficiary.position.id,
				},
				disableClose: true,
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.searchBeneficiary();
				}
			});
		}
	}

	onRemoveFile(fileId: string, event: any): void {
		event.stopPropagation();
		event.preventDefault();
		this.isLoadingFiles = true;

		this.onRemoveFileSubscription$ = this.beneficiariesService
			.deleteBeneficiaryFile({ fileId })
			.subscribe(
				async (response: MessageResponse) => {
					this.snackbarService.showSnack(response?.message, response?.type);
					this.searchBeneficiary();
					this.isLoadingFiles = false;
				},
				(error) => {
					this.isLoadingFiles = false;
					this.snackbarService.showSnack(error.error?.message, 'error');
				},
			);
	}

	onSaveFiles(): void {
		this.isLoadingFiles = true;

		const request: FormData = new FormData();

		for (const file of this.beneficiaryFiles) {
			const fileExists = this.beneficiary.files.find(
				(existingFile) => existingFile.name == file.name,
			);
			if (!fileExists) request.append('archives', file);
		}

		if (request) {
			this.onSaveFilesSubscription$ = this.beneficiariesService
				.updateBeneficiaryFiles({
					beneficiaryId: this.beneficiaryId,
					dto: request,
					inactive: this.beneficiaryType !== 'ativo',
					organizationId: this.organizationId,
				})
				.subscribe(
					async (response: MessageResponse) => {
						this.snackbarService.showSnack(response?.message, response?.type);
						this.isLoadingFiles = false;
						this.searchBeneficiary();
					},
					(response) => {
						this.throwFilesAlerts(response?.error?.message);
					},
				);
		}
	}

	onShowLog(): void {
		if (this.organizationId && this.beneficiaryId) {
			void this.router.navigate(
				[
					RouteUtils.endpoints.organizations.path,
					this.organizationId,
					RouteUtils.endpoints.beneficiaries.path,
					this.beneficiaryId,
					RouteUtils.endpoints.beneficiaries.child.history,
				],
				{
					state: {
						name: this.beneficiary.user.name.toCapitalizedCase(),
						organizationName: this.beneficiary.user.organizations_name,
					},
				},
			);
		}
	}

	processStatusName(beneficiaryType: string): string {
		if (beneficiaryType === 'ativo') {
			return 'active';
		} else if (beneficiaryType === 'inativo') {
			return 'inactive';
		} else {
			return 'retired';
		}
	}

	searchBeneficiary(): void {
		this.isLoading = true;
		this.isLoadingBenefits = true;
		this.isLoading = true;
		this.isLoadingBenefits = true;
		this.searchSubscription$ = this.beneficiariesService
			.searchBeneficiary({
				beneficiaryId: this.beneficiaryId,
				organizationId: this.organizationId,
			})
			.pipe(
				finalize(() => {
					this.isLoadingBenefits = false;
					this.isLoading = false;
				}),
			)
			.subscribe(
				(beneficiary: BeneficiaryDto) => {
					if (beneficiary) {
						this.beneficiary = beneficiary;
						this.setBeneficiary();
					} else {
						this.isLoadingBenefits = false;
						this.snackbarService.showSnack(
							'Beneficiário não encontrado.',
							'error',
						);
					}
				},
				(error: any) => {
					this.isLoadingBenefits = false;
					this.snackbarService.showSnack(error.error?.message, 'error');
				},
			);
	}

	setBeneficiary(): void {
		if (this.beneficiary) {
			const benefits = this.beneficiary.benefits
				? [...this.beneficiary.benefits]
				: [];

			this.benefitWallets = benefits;

			this.conjugatedBenefitData =
				this.beneficiary.benefits.find(
					(b) => b.type === WalletType.CONJUGATED && b.is_health && b.is_dental,
				) ||
				this.beneficiary.benefits.find(
					(b) => b.type === WalletType.CONJUGATED,
				) ||
				null;

			this.healthBenefitData =
				this.beneficiary.benefits.find(
					(benefit) => benefit.is_health && benefit.type === WalletType.DEFAULT,
				) || null;

			this.healthCongenerBenefitData =
				this.beneficiary.benefits.find(
					(benefit) =>
						benefit.is_health && benefit.type === WalletType.CONGENER,
				) || null;

			this.dentalBenefitData =
				this.beneficiary.benefits.find(
					(benefit) => benefit.is_dental && benefit.type === WalletType.DEFAULT,
				) || null;

			this.dentalCongenerBenefitData =
				this.beneficiary.benefits.find(
					(benefit) =>
						benefit.is_dental && benefit.type === WalletType.CONGENER,
				) || null;
			this.healthPolicyData =
				this.beneficiary.policies.find((policy) => policy.is_health) || null;
			this.dentalPolicyData =
				this.beneficiary.policies.find((policy) => policy.is_dental) || null;

			this.isHealthWalletPendent =
				!this.healthBenefitData?.not_aplicable &&
				!this.healthBenefitData?.benefit_id;
			this.isDentalWalletPending =
				!this.dentalBenefitData?.not_aplicable &&
				!this.dentalBenefitData?.benefit_id;

			this.isHealthCongenerWalletPending =
				!this.healthCongenerBenefitData?.not_aplicable &&
				!this.healthCongenerBenefitData?.benefit_id;
			this.isDentalCongenerWalletPending =
				!this.dentalCongenerBenefitData?.not_aplicable &&
				!this.dentalCongenerBenefitData?.benefit_id;
			this.cfgForm.setValue({
				active: this.beneficiary.active,
				inactived_at: this.beneficiary.deletedAt ?? null,
				is_deleted: this.beneficiary.is_deleted,
				retired_at: this.beneficiary.retiredAt ?? null,
			});

			if (this.beneficiary.is_deleted) {
				this.beneficiaryType = 'inativo';
			} else if (this.beneficiary.deletedAt) {
				this.beneficiaryType = this.beneficiary.retiredAt
					? 'aposentado'
					: 'excluído';
			} else {
				this.beneficiaryType = 'ativo';
			}

			if (this.beneficiaryType !== 'ativo') {
				this.cfgForm.controls.inactived_at.disable();
			}

			if (this.beneficiary.is_deleted) {
				this.cfgForm.controls.retired_at.disable();
			}
			this.filterNotifications();

			this.store.dispatch(
				fetchBeneficiaryAllBenefitsStart({
					id: this.organizationId,
					positionId: this.beneficiary.position.id,
				}),
			);
			this.store.dispatch(
				fetchBeneficiaryAllPoliciesStart({
					id: this.organizationId,
					positionId: this.beneficiary.position.id,
				}),
			);
		}

		let benefitsLoaded = false;
		let policiesLoaded = false;

		this.store
			.select(selectBeneficiaryAllBenefits)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((beneficiaryBenefits) => {
				this.healthBenefits = beneficiaryBenefits?.healths ?? [];
				this.dentalBenefits = beneficiaryBenefits?.teeths ?? [];
				benefitsLoaded = true;
				if (benefitsLoaded && policiesLoaded) {
					this.processBenefitsData();
				}
			});

		this.store
			.select(selectBeneficiaryAllPolicies)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((beneficiaryPolicies) => {
				this.healthPolicies = beneficiaryPolicies?.healths ?? [];
				this.dentalPolicies = beneficiaryPolicies?.teeths ?? [];
				policiesLoaded = true;
				if (benefitsLoaded && policiesLoaded) {
					this.processBenefitsData();
				}
			});

		this.store
			.select(selectBeneficiaryFileURL)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((url) => {
				if (url) {
					this.downloadFile(url);
					this.store.dispatch(fetchBeneficiaryFileURLClear());
				}
			});
	}

	throwFilesAlerts(message: string): void {
		if (message === UPLOAD_FORMAT_ERROR_MESSAGE) {
			this.alertRef = this.dialog.open(BeneficiaryAttachDialogComponent, {
				data: this.extensionErrorMessage,
				disableClose: true,
				maxWidth: undefined,
			});
		} else if (message === this.sizeErrMsg) {
			this.alertRef = this.dialog.open(BeneficiaryAttachDialogComponent, {
				data: this.sizeErrMsg,
				disableClose: true,
				maxWidth: undefined,
			});
		} else {
			this.isLoadingFiles = false;
			this.snackbarService.showSnack(message, 'error');
		}
	}

	updateBeneficiaryStatus(): void {
		this.cfgForm.controls.inactived_at.enable();
		const form = this.cfgForm.value;
		const request = {
			id: this.beneficiaryId,
			active: form.active,
			inactived_at: new Date(form.inactived_at).toISOString(),
			is_deleted: form.is_deleted,
			org_id: this.organizationId,
			retiredAt: form.retired_at
				? new Date(form.retired_at).toISOString()
				: null,
			status: this.processStatusName(this.beneficiaryType),
			stay_date: form.stay_date ? new Date(form.stay_date).toISOString() : null,
		};

		this.cfgForm.controls.inactived_at.disable();
		this.store
			.select(selectBeneNewStatus)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => {
				this.router
					.navigate([
						'organizacoes',
						this.organizationId,
						'beneficiarios',
						this.beneficiaryId,
					])
					.then(() => {
						location.reload();
					});
				this.store.dispatch(editBeneficiaryStatusClear());
			});
		this.store.dispatch(editBeneficiaryStatusStart({ request }));
	}

	/**
	 * Flags the second wallet in each conjugated pair to hide its delete button.
	 */
	private flagSecondConjugatedWalletForDeletion(): void {
		const conjugatedMap = new Map<string, BeneficiaryWalletDto[]>();

		for (const wallet of this.benefitWallets) {
			if (wallet.type === WalletType.CONJUGATED) {
				const group = conjugatedMap.get(wallet.name) || [];
				group.push(wallet);
				conjugatedMap.set(wallet.name, group);
			}
		}

		for (const wallets of conjugatedMap.values()) {
			if (wallets.length > 1) {
				wallets[1].hideDeleteButton = true;
			}
		}
	}

	private processBenefitsData(): void {
		const benefits = this.beneficiary?.benefits
			? [...this.beneficiary.benefits]
			: [];

		const hasHealth = benefits.some((w) => w.is_health);

		if (!hasHealth) {
			benefits.push({
				id: 'pending-health',
				benefit_id: '',
				is_dental: false,
				is_health: true,
				name: '',
				not_aplicable: false,
				partner_name: '',
				type: WalletType.DEFAULT,
				wallet_number: '',
			} as BeneficiaryWalletDto);
		}

		const hasDental = benefits.some((w) => w.is_dental);

		if (!hasDental) {
			benefits.push({
				id: 'pending-dental',
				benefit_id: '',
				is_dental: true,
				is_health: false,
				name: '',
				not_aplicable: false,
				partner_name: '',
				type: WalletType.DEFAULT,
				wallet_number: '',
			} as BeneficiaryWalletDto);
		}

		this.benefitWallets = benefits;
		if (this.benefitWallets && this.benefitWallets.length > 0) {
			this.benefitWallets.sort((a, b) => {
				const getWalletTypeOrder = (wallet: BeneficiaryWalletDto): number => {
					switch (wallet.type) {
						case WalletType.DEFAULT:
						case WalletType.CONJUGATED:
							return 0;
						case WalletType.CONGENER:
							return 1;
						default:
							return 2;
					}
				};

				const typeOrderA = getWalletTypeOrder(a);
				const typeOrderB = getWalletTypeOrder(b);

				if (typeOrderA !== typeOrderB) {
					return typeOrderA - typeOrderB;
				}

				const aIsHealth = a.is_health;
				const bIsHealth = b.is_health;

				if (aIsHealth && !bIsHealth) {
					return -1;
				}

				if (!aIsHealth && bIsHealth) {
					return 1;
				}

				return 0;
			});
		}
		this.flagSecondConjugatedWalletForDeletion();
		this.isLoading = false;
		this.isLoadingBenefits = false;
	}
}
